import { useEffect, useState, type ReactNode } from 'react'
import { useInView, usePrefersReducedMotion } from '@/hooks/useInView'
import { cn } from '@/lib/cn'

/**
 * Reveals a headline word by word, each word rising out of its own clipped
 * line box.
 *
 * The whole string stays in the DOM as one accessible text node — the split is
 * visual only, so screen readers and copy-paste see ordinary text.
 */
export function SplitText({
  text,
  className,
  wordClassName,
  step = 55,
  delay = 0,
  as: Tag = 'span',
  highlight,
}: {
  text: string
  className?: string
  wordClassName?: string
  /** Milliseconds between words. */
  step?: number
  delay?: number
  as?: 'span' | 'h1' | 'h2' | 'p'
  /** Words rendered with the gradient accent treatment. */
  highlight?: string[]
}) {
  const [ref, inView] = useInView<HTMLElement>({ threshold: 0.2 })
  const reduced = usePrefersReducedMotion()
  const words = text.split(' ')

  return (
    <Tag ref={ref as never} className={className}>
      <span className="sr-only">{text}</span>
      <span aria-hidden="true">
        {words.map((word, index) => (
          <span key={`${word}-${index}`} className="inline-block overflow-hidden align-bottom">
            <span
              className={cn(
                'inline-block will-change-transform motion-safe:transition-[transform,opacity]',
                inView || reduced ? 'translate-y-0 opacity-100' : 'translate-y-[110%] opacity-0',
                highlight?.includes(word.replace(/[^\w]/g, '')) ? 'text-gradient' : undefined,
                wordClassName,
              )}
              style={{
                transitionDuration: '820ms',
                transitionTimingFunction: 'cubic-bezier(.19,1,.22,1)',
                transitionDelay: `${delay + index * step}ms`,
              }}
            >
              {word}
            </span>
            {index < words.length - 1 ? <span>&nbsp;</span> : null}
          </span>
        ))}
      </span>
    </Tag>
  )
}

const GLYPHS = '01·—/#$%&<>[]{}∑'

/**
 * Scrambles through random glyphs before settling on the final text.
 * Used for the monospace layer markers, where it reads as a readout locking on.
 */
export function Scramble({ text, className }: { text: string; className?: string }) {
  const [ref, inView] = useInView<HTMLSpanElement>({ threshold: 0.4 })
  const reduced = usePrefersReducedMotion()
  const [shown, setShown] = useState(text)

  useEffect(() => {
    if (!inView || reduced) {
      setShown(text)
      return
    }

    let frame = 0
    let tick = 0
    const total = 18

    const run = () => {
      tick += 1
      const settled = Math.floor((tick / total) * text.length)

      setShown(
        text
          .split('')
          .map((char, index) => {
            if (index < settled || char === ' ') return char
            return GLYPHS[Math.floor(Math.random() * GLYPHS.length)]
          })
          .join(''),
      )

      if (tick < total) {
        frame = window.setTimeout(run, 34) as unknown as number
      } else {
        setShown(text)
      }
    }

    run()
    return () => window.clearTimeout(frame)
  }, [inView, reduced, text])

  return (
    <span ref={ref} className={className}>
      <span className="sr-only">{text}</span>
      <span aria-hidden="true">{shown}</span>
    </span>
  )
}

/** Wraps content in a clip-path wipe that opens as it enters view. */
export function Wipe({
  children,
  className,
  delay = 0,
}: {
  children: ReactNode
  className?: string
  delay?: number
}) {
  const [ref, inView] = useInView<HTMLDivElement>({ threshold: 0.25 })

  return (
    <div
      ref={ref}
      className={cn('motion-safe:transition-[clip-path,opacity]', className)}
      style={{
        clipPath: inView ? 'inset(0 0% 0 0)' : 'inset(0 100% 0 0)',
        opacity: inView ? 1 : 0,
        transitionDuration: '950ms',
        transitionTimingFunction: 'cubic-bezier(.19,1,.22,1)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  )
}
